import { ResponsiveWaffle } from '@nivo/waffle';
import {
  Box,
  IconButton,
  List,
  ListItem,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
// eslint-disable-next-line no-use-before-define
import React, { FC, useEffect, useState } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import * as d3 from 'd3';
import { Field } from '@models/CriteriaData';
import { CustomTooltip } from './CustomTooltip';

type WaffleData = {
  id: string;
  label: string;
  value: number;
  color?: string;
  entities?: { name: string; docCount: number }[];
  icon?: string;
};

interface WaffleProps {
  title: string;
  secondaryTitle?: string;
  secondaryIcon?: string;
  data: WaffleData[];
  onClickCallback?: (id: string | number) => void;
  defaultScheme?: boolean;
  filterQuery?: Field;
}

export const Waffle: FC<WaffleProps> = ({
  title,
  secondaryTitle = '',
  secondaryIcon,
  data,
  onClickCallback,
  defaultScheme,
  filterQuery,
}) => {
  const [totalData, setTotalData] = useState<number>(0);
  const [waffleData, setWaffleData] = useState<WaffleData[]>([]);

  useEffect(() => {
    if (data && data.length > 0) {
      setWaffleData(data);
      // @ts-ignore
      setTotalData(data.reduce((acc, obj) => acc + obj.value, 0));
    }
  }, [data]);

  const WaffleCell = ({
    position,
    size,
    x,
    y,
    color,
    fill,
    opacity,
    borderWidth,
    borderColor,
    data,
    onClick,
  }) => (
    <Tooltip
      arrow
      title={
        data ? (
          <CustomTooltip
            data={{ ...data, total: totalData }}
            title={secondaryTitle}
            icon={secondaryIcon}
            filterQuery={filterQuery}
          />
        ) : (
          'Empty'
        )
      }
    >
      <rect
        style={{ cursor: 'pointer' }}
        width={size}
        height={size}
        x={x}
        y={y}
        fill={fill || color}
        strokeWidth={borderWidth}
        stroke={borderColor}
        opacity={opacity}
        onClick={(event) => {
          onClick({ position, color, x, y, data }, event);

          if (onClickCallback) {
            onClickCallback(data.id);
          }
        }}
      />
    </Tooltip>
  );

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'legend-waffle-popover' : undefined;

  const color = d3
    .scaleOrdinal()
    .domain(waffleData.map((data) => data.id))
    .range(d3.schemeDark2);

  return (
    <>
      <Stack
        justifyContent={'center'}
        alignItems={'center'}
        direction={'row'}
        spacing={1}
      >
        <Typography variant={'h2'} sx={{ fontSize: 15, fontWeight: 400 }}>
          {title}
        </Typography>
        <IconButton onClick={handleClick}>
          <InfoIcon />
        </IconButton>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{ ml: 4 }}
        >
          <List>
            {waffleData.map((data) => (
              <ListItem key={data?.id}>
                <Box
                  sx={{
                    height: 20,
                    width: 20,
                    backgroundColor: defaultScheme
                      ? color(data?.id)
                      : data?.color,
                  }}
                />
                <Typography sx={{ p: 1, textAlign: 'center' }}>
                  {`${data.label} (${Math.ceil(
                    (data?.value / totalData) * 100,
                  )}%)`}
                </Typography>
              </ListItem>
            ))}
          </List>
        </Popover>
      </Stack>
      <ResponsiveWaffle
        data={waffleData}
        total={totalData}
        cellComponent={WaffleCell}
        rows={40}
        columns={8}
        animate={true}
        // @ts-ignore
        colors={defaultScheme ? { scheme: 'dark2' } : { datum: 'color' }}
        borderColor={{
          from: 'color',
          modifiers: [['darker', 0.3]],
        }}
        motionStiffness={90}
        motionDamping={11}
      />
    </>
  );
};
